import React from "react";
import FHIRSummary from "../../components/FHIRSummary";
import { useAuthState } from "../../components/AuthProvider";
import handleResponse from "../../utils/handleFetchResponse";
import { useMessageToast } from "../../components/MessageProvider";
import Loader from "../../components/Loader";
import Container from "../../components/Container";
import Flex from "../../components/Flex";
import "./Preview.css";

export default function Preview() {
  const toast = useMessageToast();
  const [previewLoadingState, setPreviewLoadingState] = React.useState(false);
  const [identity, setIdentity] = React.useState(null);
  const [priorAggregate, setPriorAggregate] = React.useState(null);
  const [recordUpdates, setRecordUpdates] = React.useState([]);

  // Get goldenId and previewKey from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const urlGoldenId = urlParams.get("goldenId");
  const previewKey = urlParams.get("previewKey");

  // Retrieve override_request from localStorage if previewKey exists
  let urlOverrideRequest = null;
  if (previewKey) {
    const storedData = localStorage.getItem(previewKey);
    if (storedData) {
      urlOverrideRequest = storedData;
      // Clean up localStorage after retrieving
      localStorage.removeItem(previewKey);
    }
  }

  const [goldenId, setGoldenId] = React.useState(urlGoldenId);
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const userId = user.username;

  // Fetch prior aggregate
  const fetchPriorAggregate = React.useCallback(() => {
    if (!goldenId) return Promise.reject("No golden ID");

    const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/aggregates`;
    const config = {
      method: "POST",
      body: JSON.stringify({
        ids: [
          {
            system: selectedClient.oid,
            value: goldenId,
          },
        ],
      }),
      credentials: "include",
      headers: {
        clientId: selectedClient.oid,
        "Content-Type": "application/json",
        userId: userId,
      },
    };

    return fetch(url, config)
      .then(handleResponse)
      .then((response) => {
        if (response.data && response.data.length > 0) {
          return response.data[0];
        }
        return null;
      });
  }, [goldenId, selectedClient.oid, userId]);

  // Fetch override preview
  const recordOverride = React.useCallback(() => {
    setPreviewLoadingState(true);

    // Use override_request from URL params if available, otherwise use default
    let requestPayload;
    if (urlOverrideRequest) {
      try {
        requestPayload = JSON.parse(urlOverrideRequest);
      } catch (e) {
        console.error("Error parsing override_request from URL:", e);
        toast.error("Invalid override request data");
        setPreviewLoadingState(false);
        return;
      }
    }

    if (goldenId !== "") {
      let priorData = null;
      // First fetch the prior aggregate
      fetchPriorAggregate()
        .then((data) => {
          priorData = data;
          setPriorAggregate(data);

          let sourceId = "";
          try {
            if (requestPayload?.patients?.[0]) {
              const firstPatient = JSON.parse(requestPayload.patients[0]);
              sourceId = firstPatient?.meta?.source || "";
            }
          } catch (e) {
            console.error("Error parsing patient for sourceId:", e);
          }

          // Then fetch the override preview
          const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patientsource/override`;
          const config = {
            method: "PUT",
            body: JSON.stringify(requestPayload, null, 0),
            credentials: "include",
            headers: {
              clientId: selectedClient.oid,
              "Content-Type": "application/json",
              userId: userId,
              preview: true,
              sourceId: sourceId,
            },
          };

          return fetch(url, config);
        })
        .then(handleResponse)
        .then((response) => {
          setIdentity(priorData);
          setRecordUpdates(response.recordUpdates || []);
          setPreviewLoadingState(false);
        })
        .catch((e) => {
          console.error("fetchAggregate error; ", e);
          setPreviewLoadingState(false);
          toast.error("Something went wrong while fetching the records");
        });
    }
  }, [
    goldenId,
    urlOverrideRequest,
    fetchPriorAggregate,
    selectedClient.oid,
    userId,
    toast,
  ]);

  React.useEffect(() => {
    if (previewLoadingState) {
      return;
    }
    recordOverride();
  }, [setGoldenId]);

  return (
    <Container>
      <Flex gutter={3}>
        <Flex.Item span={12}>
          <Loader loading={previewLoadingState}>
            {identity && (
              <>
                <div className="preview-banner">
                  <strong>Preview Mode:</strong> This is a preview of the
                  override changes.
                  <span className="preview-legend-item">
                    <span className="preview-legend-box preview-legend-box-new"></span>
                    New/Changed values
                  </span>
                  <span className="preview-legend-item">
                    <span className="preview-legend-box preview-legend-box-prior"></span>
                    Original values
                  </span>
                  {recordUpdates && recordUpdates.length > 0 && (
                    <span className="preview-legend-item">
                      <span className="preview-legend-box preview-legend-box-updated"></span>
                      Records being updated ({recordUpdates.length})
                    </span>
                  )}
                </div>
                <FHIRSummary
                  id={goldenId}
                  patientInfo={identity}
                  priorAggregate={priorAggregate}
                  recordUpdates={recordUpdates}
                  primaryRecords={[]}
                  linkageHistory={[]}
                  handleRecompute={() => {}}
                  canRecompute={false}
                  isInvisible={true}
                  handleRepartition={() => {}}
                  handleMultipleBirth={() => {}}
                  handleAggregateAuxDataRequest={() => {}}
                  isImdm={true}
                  isPreviewMode={true}
                />
              </>
            )}
          </Loader>
        </Flex.Item>
      </Flex>
    </Container>
  );
}
